import { useMemo, useState } from 'react';
import { habitFigure } from '../habitFigure';
import { verdictWord } from '../habitVocab';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getGoalTree,
  listTasks,
  listNotes,
  listProjectsForGoal,
  listLabels,
  updateGoal,
  updateTask,
  type GoalTreeNode,
  type Task,
} from '../api';
import { QueryState } from './QueryState';
import { ParentLink } from './ParentLink';
import { SubtaskPill } from './SubtaskPill';
import { TaskMenu, useTaskMenu } from './TaskMenu';
import { goalColorMap, resolveTaskColor } from '../colors';
import { PriorityMark } from './StatusBadge';
import { useAccountDay } from '../accountDay';
import { readableDay, readableInstant } from '../dateLabels';
import { ProgressBar } from './ProgressBar';
import { GoalCallouts } from './GoalCallouts';
import { TaskStatusControl, TaskProgressMark } from './TaskStatusControl';
import { NO_NEXT_STEP_LABEL } from '../goalVocab';
import { workStateOf } from '../workState';

/**
 * One goal, in full.
 *
 * A goal has only ever been a row in a tree: a title, a bar, and some links. All three mockup
 * directions specify a dedicated screen for it — Progress, Sub-goals, Tasks, Details, Notes — and
 * they agree on the content even where they disagree about everything else, which is about as
 * clear a signal as an undecided design can give.
 *
 * The part that could not have been built before is "Tasks: direct + inherited". A parent goal
 * usually has no tasks of its own — the work lives on its children — so a screen listing only
 * direct tasks would show the most important goals as empty.
 */

/** Depth-first search for one node, so the screen can reuse the tree the app already fetched. */
function findNode(nodes: readonly GoalTreeNode[], id: string): GoalTreeNode | null {
  for (const node of nodes) {
    if (node.goal.id === id) return node;
    const found = findNode(node.children, id);
    if (found) return found;
  }
  return null;
}

/*
  "18 Oct", or "18 Oct 2025" in another year (`dateLabels.ts`). A goal's `targetDate` is a calendar day stored as midnight UTC, so it goes through
  `readableDay`; a task's `dueAt` and the goal's `createdAt` are instants, read on the account's
  clock. Both used to be one `toLocaleDateString` on the browser's: a deadline at 13:41 UTC was
  "due 19 Oct" to a laptop in Kiritimati on an account in Warsaw, where it is the 18th.
*/
const targetDay = (iso: string | null) => (iso === null ? null : readableDay(iso));

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="stat-tile">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
      {sub !== undefined && <span className="stat-sub">{sub}</span>}
    </div>
  );
}

export function GoalDetail({
  goalId,
  onOpenTask,
  onOpenGoal,
  onClose,
}: {
  goalId: string;
  onOpenTask: (id: string) => void;
  onOpenGoal: (id: string) => void;
  onClose: () => void;
}) {
  const tree = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });
  // Direct *and* inherited: the question this screen exists to answer.
  // Cached by the task list and the pickers, so this is usually a cache read.
  const allLabels = useQuery({ queryKey: ['labels'], queryFn: listLabels });
  /*
    The same menu the list, the board and the shortlist carry.

    Every surface that shows a task should offer it — the owner found the board's done strip
    missing it, and this list was missing it for the same reason: it renders its own row rather
    than reusing one of the components that already had the handler.
  */
  const menu = useTaskMenu();

  /*
    The colour band, on the goal's own task list.

    Every task on this page serves this goal, so most rows are the same colour and the band says
    little — until one is not. A task carrying its own colour, or taking one from a label, sits
    above the goal in the chain and was indistinguishable here from the rest. The band is what says
    "this one is different", and it was the only task surface without it.
  */
  const goalClassById = goalColorMap(tree.data ?? []);
  const labelById = new Map((allLabels.data ?? []).map((l) => [l.id, l] as const));
  const colorOf = (task: Task) => {
    const fromLabel = task.labelIds.map((id) => labelById.get(id)).find((l) => l?.color != null);
    return resolveTaskColor(
      [
        { color: task.color, from: 'this task' },
        /*
          The parent, right behind the task's own choice.

          `resolveColor` asks each source at read time and never writes back, so putting the parent
          here gives subtasks live inheritance with no migration and nothing to go stale. It sits
          below an explicit colour on the subtask — that is a deliberate choice about this piece of
          work — and above the label, project and goal, which a subtask inherits from its parent
          anyway, so it only ever changes the answer when the parent was coloured by hand.
        */
        { color: task.parent?.color ?? null, from: 'its parent' },
        { color: fromLabel?.color ?? null, from: fromLabel?.name ?? '' },
      ],
      task.goalIds[0] ? (goalClassById.get(task.goalIds[0]) ?? null) : null,
    );
  };
  const labelNames = new Map((allLabels.data ?? []).map((l) => [l.id, l.name] as const));

  const tasks = useQuery({
    queryKey: ['tasks', { goalId, subtree: true }],
    queryFn: () => listTasks({ goalId, includeSubgoals: true, includeDone: true }),
  });
  // `listNotes` takes the API's own query names, not camelCase.
  const projects = useQuery({
    queryKey: ['goal-projects', goalId],
    queryFn: () => listProjectsForGoal(goalId),
  });
  const notes = useQuery({
    queryKey: ['notes', { goalId }],
    queryFn: () => listNotes({ goal_id: goalId }),
  });

  const node = useMemo(() => (tree.data ? findNode(tree.data, goalId) : null), [tree.data, goalId]);

  const queryClient = useQueryClient();
  const [showDone, setShowDone] = useState(false);
  const [showParked, setShowParked] = useState(false);
  /*
    The account's day, as everywhere else that plans something.

    This built the browser's out of `getFullYear`/`getMonth`/`getDate`, and used it twice: to write
    `scheduledDate` and to decide whether to draw the "today" link at all — so on a laptop a day
    behind the account the link was offered for work already planned for today, and hidden on work
    planned for yesterday.
  */
  const { today, timezone } = useAccountDay();
  const instantDay = (iso: string) => readableInstant(iso, timezone, 'day');
  const planToday = useMutation({
    mutationFn: (id: string) => updateTask(id, { scheduledDate: today }),
    onSuccess: () => queryClient.invalidateQueries(),
  });
  /*
    The way back out of a pause, on the goal's own screen.

    The tree row has offered this since Phase 39; the page you open to *look at* the goal offered
    nothing, so the only route back was to go back to the list and find the row again. Same call
    as the row's, deliberately — "resume" has to mean one thing.
  */
  const resume = useMutation({
    mutationFn: () => updateGoal(goalId, { status: 'active' }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  if (tree.isPending || tree.isError) return <QueryState query={tree} />;
  if (!node) {
    return (
      <section className="card">
        <p className="muted">That goal is gone.</p>
        <button onClick={onClose}>Back to goals</button>
      </section>
    );
  }

  const { goal, progress, pace, momentum, habits, children } = node;
  /*
    Three piles, by the one rule core counts with (`workState`).

    `open` was `status !== 'done' && status !== 'dropped'`, so a parked task was drawn as an
    ordinary open row — unticked box, a *today* offer — and counted in the *Open* tile, while its
    project card said *1 parked* about the same task. Parked has a pile of its own now, folded
    like the done one, and the tile's figures are the server's `openWeight` and `parkedWeight`.
  */
  const rows = tasks.data ?? [];
  const open = rows.filter((t: Task) => workStateOf(t) === 'open');
  const parked = rows.filter((t: Task) => workStateOf(t) === 'parked');
  const done = rows.filter((t: Task) => workStateOf(t) === 'done');
  const taskCount = (n: number) => `${n} ${n === 1 ? 'task' : 'tasks'}`;

  return (
    <section
      className={`goal-detail${goal.live ? '' : ' is-paused'}`}
      data-testid="goal-detail"
      data-goal-id={goal.id}
    >
      <div className="row gap">
        <button className="link" onClick={onClose}>
          ← All goals
        </button>
      </div>

      {/*
        The goal's colour, on the goal's own page.

        It identifies this goal on every task row, every chip and every board card — and its own
        screen was the one place that never showed it, so arriving here from a violet band gave no
        confirmation you had landed on the right thing.
      */}
      <h2>
        <span className={`goal-swatch ${goalClassById.get(goal.id) ?? ''}`} aria-hidden="true" />
        {goal.title}
      </h2>
      {goal.descriptionMd.trim() !== '' && <p className="muted">{goal.descriptionMd}</p>}

      {/*
        A goal you set aside says so here, above everything derived from it.

        The word `paused` used to appear exactly once on this page: as a value in the Details
        table, 240px below the fold-line of a full-width green 46% bar, two tasks each offering a
        live "today" button, and a habit carrying a verdict. Every affordance above it was written
        for a goal that is running, so the page for a goal somebody had explicitly stopped was
        indistinguishable from the page for one they had not — and the one control that would undo
        the decision, resume, existed only back on the list.

        `goal.live` rather than `status !== 'paused'`: whether a goal is still being pursued is
        core's `isLive`, and the server sends the answer. The *word* still comes from `status`,
        because "paused" and "done" are different decisions and a banner that called them both
        "not live" would be the same defect wearing a predicate.
      */}
      {goal.status === 'paused' && (
        <p className="goal-paused" data-testid="goal-paused">
          <span className="pill warn">paused</span>
          <span className="subtext">
            Set aside — its work is not offered on the day, and nothing here is being judged.
          </span>
          <button
            type="button"
            data-testid="resume-goal"
            disabled={resume.isPending}
            onClick={() => resume.mutate()}
          >
            Resume
          </button>
        </p>
      )}

      {/*
        Same rule as the tree row: a goal with no task work makes no completion claim. This screen
        is where the contradiction was clearest — "0%" in a tile, directly above a Details block
        listing 210 t of barbell tonnage and 341 km run as the habits serving it.
      */}
      {progress.totalWeight === 0 && habits.length > 0 && (
        <p className="banner">
          Tracked by habits, not by tasks — there is no work here to finish, so there is no
          percentage to report. The habits carrying it are below.
        </p>
      )}

      <div className="stat-row">
        {!(progress.totalWeight === 0 && habits.length > 0) && (
          <Tile label="Progress" value={`${progress.percent}%`} sub="by points" />
        )}
        <Tile label="Sub-goals" value={String(children.length)} />
        {/*
          Weight rather than a task count: a goal mixing pointed and unpointed work is measured in
          points, and showing a count here would disagree with the bar above it. That reasoning is
          right and the tiles keep the weight — but `Open` and `Done` were bare nouns standing
          directly above a list of tasks, so the number read as a count of that list and disagreed
          with it. One goal reported "5 Open · 7 Done" over four open and three done rows; another
          reported "32 Open" where 32 was the whole account's task total.

          So the unit is named, and the count it is *not* is given underneath. Both readings are
          available, and neither contradicts the bar or the list.
        */}
        {/*
          Open is `openWeight`, not `totalWeight - completedWeight`: that remainder is open *plus
          parked*, and printing it here is how a goal of three done, one active and three resting
          read as "4 things left". The shelf is named on the same tile rather than given a fourth
          one — it is part of what is not done, and the bar still counts it.
        */}
        <Tile
          label="Open"
          value={`${Math.round(progress.openWeight)} pts`}
          sub={
            parked.length > 0
              ? `${taskCount(open.length)} · ${parked.length} parked`
              : taskCount(open.length)
          }
        />
        <Tile
          label="Done"
          value={`${Math.round(progress.completedWeight)} pts`}
          sub={taskCount(done.length)}
        />
      </div>

      {!(progress.totalWeight === 0 && habits.length > 0) && (
        <ProgressBar percent={progress.percent} title={`${progress.percent}% by weight`} />
      )}

      {/*
        What the app has noticed, and what to press about it.

        These three callouts were written in the Goals tree and rendered only there, so the screen
        you open to *work on* a goal showed a bare "Pace: behind" and offered no remedy — while the
        list one click away said "dormant · 50d — Pick one small thing to start it again" with a
        working button. The diagnosis was on one screen and the answer on the other. Shared now,
        so neither can gain a state the other lacks.
      */}
      <GoalCallouts goal={goal} pace={pace} momentum={momentum} />

      {children.length > 0 && (
        <>
          <h3>Sub-goals</h3>
          <ul className="plain subgoal-list" data-testid="subgoal-list">
            {children.map((child) => (
              <li key={child.goal.id}>
                <button className="link" onClick={() => onOpenGoal(child.goal.id)}>
                  {child.goal.title}
                </button>
                <span className="task-meta">
                  <span className="pill">{child.progress.percent}%</span>
                  {child.goal.targetDate && (
                    <span className="pill">due {targetDay(child.goal.targetDate)}</span>
                  )}
                  {child.pace.nextActionState === 'ready_to_finish' ? (
                    <span className="pill ok">ready to finish</span>
                  ) : (
                    child.pace.needsNextAction && (
                      <span className="pill warn">{NO_NEXT_STEP_LABEL}</span>
                    )
                  )}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <h3>
        Tasks <span className="subtext">direct and inherited</span>
      </h3>
      <QueryState query={tasks} />
      {tasks.data?.length === 0 && (
        <p className="muted">
          Nothing is attached to this goal or anything under it. That is what “{NO_NEXT_STEP_LABEL}”
          means on the goals screen.
        </p>
      )}
      <ul className="plain goal-task-list" data-testid="goal-tasks">
        {open.map((task) => (
          <li
            key={task.id}
            data-testid="goal-task"
            className={colorOf(task).colorClass ?? ''}
            title={colorOf(task).colorClass ? `Colour from ${colorOf(task).from}` : undefined}
            onContextMenu={(e) => menu.open(e, task)}
          >
            {/*
              The row's own controls, which it had none of.

              This page reported "58%, pace: behind, four open tasks" and gave no way to touch any
              of them — a DOM sweep found zero buttons in the main column beyond the task titles.
              The same box the Tasks list carries, and the same one-press plan the dormant nudge
              offers, so a goal can be worked from the screen named after it.
            */}
            <TaskStatusControl task={task} />
            <button className="link" onClick={() => onOpenTask(task.id)}>
              {task.title}
            </button>
            {task.parent && <ParentLink parent={task.parent} onOpen={onOpenTask} />}
            {/*
              Not offered on a goal that is not live: a page that has just said "set aside" must
              not, four lines lower, invite you to schedule its work for this morning. The DOM
              probe that found this counted two of these on the paused guitar goal, each titled
              "Plan “…” for today". Resume is the button on this page now, and it is above them.
            */}
            {goal.live && task.scheduledDate !== today && (
              <button
                type="button"
                className="link plan-today"
                data-testid="plan-today"
                title={`Plan “${task.title}” for today`}
                disabled={planToday.isPending}
                onClick={() => planToday.mutate(task.id)}
              >
                today
              </button>
            )}
            <span className="task-meta">
              <SubtaskPill task={task} />
              {/*
                The same state the task list shows, minus the goal chip.

                This showed a blocked badge and nothing else, so a task that was *in progress* read
                identically to one nobody had started — on the screen you open to ask how a goal is
                going. Priority and labels were missing for the same reason. The goal chip is the
                one thing deliberately left out: every task on this page serves this goal, so
                naming it on each row says nothing.

                It is the offer as well as the state now: on a row that has not started, the same
                slot is the button that starts it. This row is one flex line whose status box is
                already 24px tall, so unlike the task list's the mark costs nothing here.
              */}
              <TaskProgressMark task={task} />
              <PriorityMark priority={task.priority} />
              {task.complexityPoints && <span className="pill">{task.complexityPoints} pts</span>}
              {task.estimateMinutes && <span className="pill">{task.estimateMinutes}m</span>}
              {task.dueAt && <span className="pill">due {instantDay(task.dueAt)}</span>}
              {task.labelIds
                .map((id) => labelNames.get(id))
                .filter((name): name is string => name !== undefined)
                .slice(0, 2)
                .map((name) => (
                  <span key={name} className="pill label-chip" title={`Label: ${name}`}>
                    {name}
                  </span>
                ))}
            </span>
          </li>
        ))}
        {/*
          The shelf, folded like the done pile and tagged when drawn.

          A parked task is not one you are being asked to do today, so it gets no *today* offer
          and does not sit among the open rows. The tag is the word the project card uses for the
          same task, so the two screens name it alike.
        */}
        {parked.length > 0 && (
          <li data-testid="goal-parked-count">
            <button
              type="button"
              className="link"
              aria-expanded={showParked}
              onClick={() => setShowParked((v) => !v)}
            >
              {parked.length} parked
            </button>
          </li>
        )}
        {showParked &&
          parked.map((task) => (
            <li
              key={task.id}
              data-testid="goal-parked-task"
              onContextMenu={(e) => menu.open(e, task)}
            >
              <TaskStatusControl task={task} />
              <button className="link" onClick={() => onOpenTask(task.id)}>
                {task.title}
              </button>
              <span className="task-meta">
                <span className="pill">parked</span>
              </span>
            </li>
          ))}
        {/*
          Finished work, reachable.

          "3 done" was grey, flat and unclickable — the only record of what the goal had achieved,
          on the screen that exists to ask how it is going, and there was no way to see it.
        */}
        {done.length > 0 && (
          <li data-testid="goal-done-count">
            <button
              type="button"
              className="link"
              aria-expanded={showDone}
              onClick={() => setShowDone((v) => !v)}
            >
              {done.length} done
            </button>
          </li>
        )}
        {showDone &&
          done.map((task) => (
            <li key={task.id} data-testid="goal-done-task">
              <TaskStatusControl task={task} />
              <button className="link struck" onClick={() => onOpenTask(task.id)}>
                {task.title}
              </button>
            </li>
          ))}
      </ul>

      {/*
        The other end of the link.

        A goal that several projects serve should say so — otherwise the relationship is only
        visible from the project, and the goal screen keeps looking emptier than the work behind
        it. Deliberately below the task list: it is context, not progress.
      */}
      {(projects.data?.length ?? 0) > 0 && (
        <>
          <h3>
            Projects <span className="subtext">serving this goal</span>
          </h3>
          <ul className="plain" data-testid="goal-projects">
            {projects.data?.map((p) => (
              <li key={p.id}>
                {p.title}
                <span className="task-meta">
                  <span className="pill">{p.status}</span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <h3>Details</h3>
      <dl className="goal-details">
        <dt>Status</dt>
        <dd>{goal.status}</dd>
        <dt>Target date</dt>
        <dd>{targetDay(goal.targetDate) ?? 'None'}</dd>
        <dt>Pace</dt>
        <dd>
          {/*
            The row 6847fba missed. That commit taught the headline, the bar and the callouts to
            say "tracked by habits" for a goal with no task work, and this table one section below
            went on printing "on track" — the same contradiction, on the same screen, from the
            surface that looks most like a statement of fact.
          */}
          {pace.state === 'no_target'
            ? 'Not judged — no target date'
            : pace.state === 'not_judged'
              ? 'Not judged — tracked by habits'
              : pace.state.replace('_', ' ')}
        </dd>
        <dt>Depth</dt>
        <dd>{goal.depth === 0 ? 'Top level' : `Level ${goal.depth + 1}`}</dd>
        <dt>Habits serving it</dt>
        <dd>
          {/*
            The habit, with how it is going.

            This printed a comma-separated list of names. `GoalHabit` has carried `actual`,
            `target`, `period` and `state` the whole time — the goal page simply threw them away,
            so the screen you open to ask how a goal is doing said only that a habit exists.

            It matters more here than on most surfaces: `needsNextAction` treats a goal with a
            supporting habit as *not* stalled, so the habit is load-bearing for the goal's own
            verdict, and it was the one thing on the page with no state attached.
          */}
          {habits.length === 0
            ? 'None'
            : habits.map((h) => (
                <span key={h.id} className="goal-habit">
                  <strong>{h.name}</strong>{' '}
                  <span className="subtext">
                    {/* The one figure — see `habitFigure`. It printed `actual` raw, so a week of
                        16.8 + 1.1 read *17.900000000000002 t of 18*, and an unread reading read
                        *0 % of 14 this day* (`01a03813-b97d`). */}
                    {habitFigure(h, { window: h.period, asOf: today, today })}
                    {/*
                      The verdict, not the raw state. "0 km of 30 this week · on track" was the
                      line printed directly under a goal that also claimed to be on track, and it
                      is the sentence the maximizer quoted: the app asserting twice over that
                      nothing having happened is fine. `too_early` says what is actually true.

                      Withheld entirely on a goal that is not live, which is the same rule the
                      whole page now follows: a verdict is an answer to "how is this goal going",
                      and the reader has already said it is not going. The counts stay — the habit
                      may well still be running under its own steam — but "0 sessions of 4 this
                      week · behind" under a banner reading *paused* is the app arguing with the
                      decision it has just acknowledged.
                    */}
                    {goal.live && (
                      <>
                        {' · '}
                        {/* Through `verdictWord`: a reading unread for months said *too early to
                            tell* here (`01a15d62-4f6d`). */}
                        {verdictWord(h, { period: h.period, asOf: today })}
                      </>
                    )}
                  </span>
                </span>
              ))}
        </dd>
        <dt>Created</dt>
        <dd>{instantDay(goal.createdAt)}</dd>
      </dl>

      <h3>Notes</h3>
      {notes.data?.length === 0 && <p className="muted">No notes on this goal.</p>}
      <ul className="plain">
        {notes.data?.map((note) => (
          <li key={note.id} data-testid="goal-note">
            <strong>{note.title || 'Untitled'}</strong>
            {note.bodyMd && <span className="subtext"> {note.bodyMd.slice(0, 140)}</span>}
          </li>
        ))}
      </ul>

      <TaskMenu target={menu.target} onClose={menu.close} onOpenTask={onOpenTask} />
    </section>
  );
}
